import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import { 
  MapPin, 
  Calendar, 
  Sparkles, 
  Info,
  Palmtree, 
  Coffee, 
  Camera,
  Star,
  Map as MapIcon,
  ChevronRight,
  Mail,
  Send,
  Download
} from 'lucide-react';
import { destinationsData } from '../data/destinations';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DestinationLanding() {
  const { slug } = useParams();
  const [dbDestination, setDbDestination] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Backwards compatibility with static data
  const staticData = destinationsData[slug];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Destination from DB
        const destRes = await axios.get(`${API}/destinations/${slug}`);
        setDbDestination(destRes.data);
        
        // 2. Fetch associated trips
        const tripsRes = await axios.get(`${API}/trips`);
        const searchName = destRes.data.name || staticData?.title || slug;
        const filteredTrips = tripsRes.data.filter(t => 
          t.is_active && (
            t.destination?.toLowerCase().includes(searchName.toLowerCase()) ||
            t.title.toLowerCase().includes(searchName.toLowerCase())
          )
        );
        setTrips(filteredTrips);
      } catch (err) {
        console.warn('Destination not in DB or error, using static fallback if available');
        if (staticData) {
          try {
            const tripsRes = await axios.get(`${API}/trips`);
            const filteredTrips = tripsRes.data.filter(t => 
              t.is_active && (
                t.destination?.toLowerCase().includes(staticData.title.toLowerCase()) ||
                t.title.toLowerCase().includes(staticData.title.toLowerCase())
              )
            );
            setTrips(filteredTrips);
          } catch (e) {
            console.error('Error fetching fallback trips:', e);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [slug, staticData?.title]);

  const destination = dbDestination ? {
    title: dbDestination.name,
    heroTitle: dbDestination.title,
    heroSubtitle: dbDestination.description,
    heroImage: dbDestination.image_url,
    heroVideo: dbDestination.hero_video_url,
    // Add defaults for missing fields in DB that exist in staticData
    introTitle: dbDestination.introTitle || staticData?.introTitle || `¿Por qué viajar a ${dbDestination.name}?`,
    introText: dbDestination.introText || staticData?.introText || dbDestination.description,
    curiosities: (dbDestination.curiosities && dbDestination.curiosities.length > 0) 
      ? dbDestination.curiosities 
      : (dbDestination.content_blocks?.find(b => b.type === 'curiosities')?.items || staticData?.curiosities || []),
    events: (dbDestination.events && dbDestination.events.length > 0)
      ? dbDestination.events
      : (dbDestination.content_blocks?.find(b => b.type === 'events')?.items || staticData?.events || []),
    mapUrl: dbDestination.mapUrl || staticData?.mapUrl
  } : staticData;

  const handleGuideRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        guide: destination?.title || slug
      });
      toast.success("¡Solicitud recibida! En breve recibirás la guía en tu correo electrónico.");
      setEmail('');
    } catch (err) {
      toast.error("No pudimos procesar tu solicitud. Inténtalo más tarde.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!destination) {
    return (
      <Layout>
        <div className="section container-custom text-center py-40">
          <h1 className="text-4xl font-serif mb-4">Destino no encontrado</h1>
          <Link to="/" className="text-secondary hover:underline">Volver al inicio</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Landing - Ultra Limpio por petición del usuario */}
      <div className="relative h-[55vh] md:h-[65vh] flex items-center justify-center text-center overflow-hidden">
        {destination.heroVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={destination.heroVideo} type="video/mp4" />
          </video>
        ) : (
          <img
            src={destination.heroImage}
            alt={`Destino ${destination.title}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/20" />
        
        <div className="container-custom relative z-10 text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-7xl md:text-9xl font-serif mb-0 drop-shadow-2xl animate-fade-in uppercase tracking-tighter">
              {destination.title}
            </h1>
            <div className="w-24 h-1 bg-accent mx-auto mt-6 shadow-xl" />
          </div>
        </div>
      </div>

      {/* Intro Personalizada */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="space-y-10">
              <div>
                <span className="text-accent font-bold tracking-widest uppercase text-sm block mb-4">
                  Experiencia {destination.title}
                </span>
                <h2 className="text-4xl md:text-5xl font-serif text-primary mb-8 leading-tight">
                  {destination.heroTitle}
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  {destination.heroSubtitle}
                </p>
              </div>

              <div className="bg-secondary/5 p-10 rounded-[3rem] border border-secondary/10 relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-2xl font-serif text-primary mb-4">{destination.introTitle}</h3>
                  <p className="text-muted-foreground mb-8 leading-relaxed">
                    {destination.introText}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors text-accent">
                        <Sparkles className="w-6 h-6" />
                      </div>
                      <span className="font-bold text-sm text-primary">Experiencias VIP</span>
                    </div>
                    <div className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors text-accent">
                        <Camera className="w-6 h-6" />
                      </div>
                      <span className="font-bold text-sm text-primary">Guías Fotógrafos</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
               <h3 className="text-3xl font-serif text-primary mb-6 flex items-center gap-3 italic">
                  Curiosidades y Secretos
               </h3>
               {destination.curiosities?.map((cur, idx) => (
                 <div key={idx} className={`p-8 rounded-[2rem] border transition-all duration-300 hover:shadow-xl ${idx === 0 ? 'bg-primary text-white shadow-lg' : 'bg-slate-50 border-slate-100 hover:bg-white'}`}>
                    <h4 className={`text-xl font-serif mb-3 ${idx === 0 ? 'text-accent' : 'text-primary'}`}>{cur.title}</h4>
                    <p className={`text-sm leading-relaxed ${idx === 0 ? 'text-white/80' : 'text-muted-foreground'}`}>{cur.text}</p>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calendario de Eventos */}
      <section className="py-24 bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-serif text-primary mb-4 italic">El mejor momento para viajar</h2>
            <p className="text-muted-foreground">Cada estación ofrece una cara distinta de {destination.title}. Elige la que mejor encaje con tu ritmo.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {destination.events.map((event, idx) => (
              <div key={idx} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 group">
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6 group-hover:bg-secondary group-hover:text-white transition-colors">
                  <Calendar className="w-7 h-7" />
                </div>
                <span className="text-accent font-bold text-xs uppercase tracking-[0.2em] block mb-3">{event.month}</span>
                <h4 className="text-xl font-serif text-primary mb-4 leading-tight">{event.event}</h4>
                <p className="text-muted-foreground text-sm italic leading-relaxed">
                   "{event.description}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Viajes Disponibles */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-primary mb-4 italic">Nuestras Rutas en {destination.title}</h2>
            <div className="w-20 h-1 bg-accent mx-auto" />
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : trips.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {trips.map(trip => (
                <Link 
                  key={trip.id} 
                  to={`/viaje/${trip.id}`}
                  className="group bg-white rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 flex flex-col h-full"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img 
                      src={trip.image_url || trip.main_image} 
                      alt={trip.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-[10px] font-bold text-primary uppercase tracking-[0.2em] shadow-lg">
                      {trip.duration}
                    </div>
                  </div>
                  <div className="p-10 flex flex-col flex-1">
                    <h3 className="text-2xl font-serif text-primary mb-4 leading-tight group-hover:text-accent transition-colors">
                      {trip.title}
                    </h3>
                    <p className="text-muted-foreground text-sm line-clamp-3 mb-8 flex-1 leading-relaxed">
                      {trip.short_description || trip.description}
                    </p>
                    <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-1">Inversión desde</span>
                        <span className="text-2xl font-bold text-primary">{trip.price?.toLocaleString('es-ES')}€</span>
                      </div>
                      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-sm">
                        <ChevronRight className="w-6 h-6" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-slate-50 rounded-[4rem] border-2 border-dashed border-slate-200 shadow-inner max-w-4xl mx-auto px-10">
               <Info className="w-16 h-16 text-slate-300 mx-auto mb-8" />
               <h3 className="text-3xl font-serif text-primary mb-6 italic">Estamos trazando nuevas rutas</h3>
               <p className="text-xl text-muted-foreground mb-12 max-w-xl mx-auto leading-relaxed italic">
                 "La perfección lleva tiempo. Estamos terminando de diseñar nuestras próximas experiencias exclusivas en <strong>{destination.title}</strong>."
               </p>
               <Link to="/contacto" className="btn-primary">
                 <Send className="w-5 h-5 mr-3" />
                 Solicitar Itinerario a medida
               </Link>
            </div>
          )}
        </div>
      </section>

      {/* Mapa y Geolocalización */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 skew-x-12 transform translate-x-1/2" />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="text-white">
              <div className="w-16 h-1 bg-accent mb-8 shadow-lg shadow-accent/20" />
              <h2 className="text-4xl md:text-5xl font-serif mb-8 flex items-center gap-5">
                <MapIcon className="text-accent w-12 h-12" />
                Situación <br className="hidden md:block"/> Geográfica
              </h2>
              <p className="text-xl text-white/70 mb-12 leading-relaxed font-light">
                {destination.title} ofrece una variedad climática y paisajística única. Desde cumbres nevadas hasta playas vírgenes, es un mosaico de experiencias naturales.
              </p>
              
              <div className="space-y-4">
                 <div className="flex items-center gap-4 p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                    <Sparkles className="w-6 h-6 text-accent" />
                    <span className="text-white/90">Ubicación estratégica y conectividad total.</span>
                 </div>
                 <div className="flex items-center gap-4 p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                    <Sparkles className="w-6 h-6 text-accent" />
                    <span className="text-white/90">Diversidad de ecosistemas en distancias cortas.</span>
                 </div>
              </div>
            </div>

            <div className="rounded-[4rem] overflow-hidden shadow-2xl border-8 border-white/10 h-[550px] relative">
              <iframe
                src={destination.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title={`Map of ${destination.title}`}
                className="grayscale opacity-90 hover:grayscale-0 transition-all duration-1000"
              ></iframe>
              <div className="absolute inset-0 pointer-events-none border-[20px] border-primary/5 shadow-inner" />
            </div>
          </div>
        </div>
      </section>

      {/* Guide Subscription Section - AUTOMATED */}
      <section className="py-32 bg-secondary/5 relative overflow-hidden">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto text-center bg-white p-12 md:p-24 rounded-[4rem] shadow-2xl border border-secondary/20 relative">
             <div className="absolute -top-10 -right-10 w-64 h-64 bg-accent/5 rounded-full blur-3xl" />
             <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />
             
             <div className="relative z-10">
               <div className="inline-flex items-center justify-center p-5 bg-accent/10 rounded-full mb-8 text-accent">
                 <Download className="w-10 h-10 animate-bounce" />
               </div>
               <h2 className="text-4xl md:text-7xl font-serif text-primary mb-10 leading-tight italic">
                  Tu Guía Secreta de <br/> {destination.title}
               </h2>
               <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
                 ¿Quieres descubrir los lugares donde los locales desayunan y los rincones que no aparecen en Instagram? Déjanos tu email y te mandamos nuestra guía VIP en PDF ahora mismo.
               </p>
               
               <form onSubmit={handleGuideRequest} className="flex flex-col md:flex-row gap-5 max-w-2xl mx-auto relative group">
                 <div className="flex-1 relative">
                    <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 w-6 h-6" />
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Tu mejor email..." 
                      className="w-full pl-16 pr-8 py-6 rounded-full border border-slate-200 focus:outline-none focus:ring-4 focus:ring-secondary/20 transition-all text-xl shadow-inner"
                      required
                    />
                 </div>
                 <button 
                  type="submit"
                  disabled={submitting}
                  className="bg-secondary text-white px-12 py-6 rounded-full font-bold hover:bg-secondary/90 transform hover:scale-105 transition-all shadow-2xl shadow-secondary/20 flex items-center justify-center gap-3 disabled:opacity-50 min-w-[240px]"
                 >
                   {submitting ? "Enviando..." : "Mandar Guía PDF"}
                   <ChevronRight className="w-6 h-6" />
                 </button>
               </form>
               
               <p className="mt-10 text-sm text-muted-foreground italic flex items-center justify-center gap-2">
                 <Sparkles className="w-4 h-4 text-accent" />
                 Recibirás el PDF al instante y pasarás a formar parte de nuestra lista VIP.
               </p>
             </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
